import { AnalyticsEvent } from "../analytics/analyticsEvents.js";
import type { DomainControllers } from "../app/createControllers.js";
import type { ShellSystems } from "../app/createShellSystems.js";
import {
  pickControllerSwap,
  progressFromSave,
  type ProgressState,
} from "../app/saveSwap.js";
import type { TabRouter } from "../app/TabRouter.js";
import type { TutorialRunner } from "@yjsgames/phaser-tutorial";
import type { BattlefieldRunner } from "./BattlefieldRunner.js";
import { decodeSaveData, encodeSaveData } from "../systems/SaveCodec.js";
import type { ModuleBrickSaveData } from "../models/ModuleBrickSaveData.js";
import { LEGACY_MAIN_TUTORIAL_AUTO_START_ENABLED } from "../tutorial/tutorialPolicy.js";

export interface GameScenePersistenceDeps {
  readonly controllers: () => DomainControllers;
  readonly setControllers: (controllers: DomainControllers) => void;
  readonly progress: () => ProgressState;
  readonly setProgress: (progress: ProgressState) => void;
  readonly shell: () => ShellSystems;
  readonly tabRouter: () => TabRouter;
  readonly tutorialRunner: () => TutorialRunner;
  readonly runner: () => BattlefieldRunner;
  readonly applySettings: () => Promise<void>;
  readonly applyShellLayout: () => void;
  readonly buildUi: () => void;
  readonly setupDailyReset: () => void;
  readonly setupCooldown: () => void;
  readonly refreshTabLocks: () => void;
  readonly refreshAll: () => void;
}

export async function loadAndApplyGameScene(deps: GameScenePersistenceDeps): Promise<void> {
  let data = await deps.controllers().saveSystem.load();
  await deps.applySettings();
  if (data && shouldResetGuideQuestForDev()) {
    data = { ...data, guideQuest: { progress: {} } };
    await deps.controllers().saveSystem.save(data);
  }
  if (data) applyGameSceneSaveData(deps, data);

  deps.applyShellLayout();
  deps.shell().bannerController.evaluate();
  deps.buildUi();
  deps.setupDailyReset();
  deps.setupCooldown();

  if (!data) {
    deps.controllers().analytics.log(AnalyticsEvent.FirstSession);
    deps.shell().grid.grantModule("double", {
      shapeId: "straight_h",
      footprintId: "single",
    });
  }

  deps.runner().buildChapterGrid();
  deps.runner().resetFireTimer();
  deps.tabRouter().setActive(deps.tabRouter().current());
  deps.shell().crazyGamesBannerController?.evaluate();
  deps.refreshTabLocks();
  deps.refreshAll();
  if (
    LEGACY_MAIN_TUTORIAL_AUTO_START_ENABLED &&
    !deps.controllers().guideQuest.active()
  ) {
    deps.tutorialRunner().startScenario("main");
  }
}

export function shouldResetGuideQuestFromSearch(search: string, dev = import.meta.env.DEV): boolean {
  return dev && new URLSearchParams(search).has("resetGuideQuest");
}

function shouldResetGuideQuestForDev(): boolean {
  if (typeof window === "undefined") return false;
  return shouldResetGuideQuestFromSearch(window.location.search);
}

export function applyGameSceneSaveData(
  deps: Pick<GameScenePersistenceDeps, "controllers" | "setControllers" | "setProgress" | "tabRouter">,
  data: ModuleBrickSaveData,
): void {
  const s = decodeSaveData(data);
  deps.setControllers({ ...deps.controllers(), ...pickControllerSwap(s) });
  deps.setProgress(progressFromSave(s));
  deps.tabRouter().resetToBattle();
}

export function toGameSceneSaveData(
  controllers: DomainControllers,
  progress: ProgressState,
  activeTab: string,
): ModuleBrickSaveData {
  return encodeSaveData({ ...controllers, ...progress, activeTab }, Date.now());
}
